import {
  type ConditionalOperator,
  findConditionRuleProblem,
  hasCapacityVariantEnabled,
  IFRAME_HEIGHT_DEFAULT,
  type IframeAspectRatio,
  iframeUrlProblem,
  isSelfReferentialConditionSource,
  isValidIframeHeight,
  pickT,
} from "@repo/shared";
import {
  ASSET_CLASS_FORECAST_HORIZONS,
  type AssetClassForecastHorizon,
  type ProductCostsSource,
  type QuizSettings,
} from "@repo/shared/types";
import { useEffect, useState } from "react";
import { conditionRuleProblemMessage } from "@/components/conditional-display/condition-rule-problem-message";
import { isAppOrigin } from "@/components/sandboxed-iframe";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCancel,
  DialogDescription,
  DialogScrollContent,
  DialogScrollFooter,
  DialogScrollHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MarkdownEditor } from "@/components/ui/markdown-editor";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApiError, patch } from "@/lib/api/_client";
import { useFlowConfiguredAssessmentTypes } from "@/lib/hooks/use-configured-assessment-types";
import { useFlowQuizzes } from "@/lib/hooks/use-quizzes";
import { log } from "@/lib/logger/browser-logger";
import type {
  AssetAllocationConfig,
  AssetClassForecastConfig,
  ClientClassificationConfig,
  ComponentConfig,
  ComponentStyleType,
  CostBenefitConfig,
  CostChartConfig,
  CostTableConfig,
  EmbedConfig,
  EquityShareChartConfig,
  ExpectedValueConfig,
  FileConfig,
  FinancialSituationConfig,
  GoalInputConfig,
  HistoricalReturnConfig,
  IframeConfig,
  ImageConfig,
  ModalConfig,
  PortfolioCostsConfig,
  PortfolioDisplayConfig,
  PortfolioForecastConfig,
  PortfolioInputConfig,
  PortfolioXrayConfig,
  ProductAssessmentConfig,
  ProductCostsConfig,
  QuizConfig,
  RiskAssessmentConfig,
  SpacerConfig,
  TextBlockConfig,
  VolatilityChartConfig,
  YoutubeVideoConfig,
} from "@/lib/types/flow";
import { textBlockContent } from "@/lib/utils/text-block-content";
import { HorizonChartConfigForm } from "../add-component-dialog/horizon-chart-config-form";
import { SumSubVerificationConfigForm } from "../add-component-dialog/sumsub-verification-config-form";
import { ConditionalVisibilityFields } from "../conditional-visibility-fields";
import { EmbedEditor } from "../pages/detail/component-editors/embed-editor";
import { FileEditor } from "../pages/detail/component-editors/file-editor";
import { IframeEditor } from "../pages/detail/component-editors/iframe-editor";
import { ImageEditor } from "../pages/detail/component-editors/image-editor";
import { ModalEditor } from "../pages/detail/component-editors/modal-editor";
import { YoutubeVideoEditor } from "../pages/detail/component-editors/youtube-video-editor";
import { EMPTY_QUIZ_SETTINGS, QuizSettingsEditor, quizSettingsError } from "../quiz-editor";
import { T } from "./component-settings-dialog.i18n";
import type { ComponentSettingsDialogProps } from "./component-settings-types";
import { SettingsAssetAllocationForm } from "./settings-asset-allocation-form";
import { SettingsCostChartForm } from "./settings-cost-chart-form";
import { SettingsCostTableForm } from "./settings-cost-table-form";
import {
  SettingsCustomPortfolioFieldsForm,
  SettingsCustomPortfolioForm,
} from "./settings-custom-portfolio-form";
import { SettingsExpectedValueForm } from "./settings-expected-value-form";
import { SettingsFinancialSituationForm } from "./settings-financial-situation-form";
import { SettingsPortfolioDisplayForm } from "./settings-portfolio-display-form";
import { SettingsPortfolioForecastForm } from "./settings-portfolio-forecast-form";
import { SettingsPortfolioInputForm } from "./settings-portfolio-input-form";
import { SettingsProductCostsForm } from "./settings-product-costs-form";
import { SettingsStyleForm } from "./settings-style-form";
import { SettingsXrayForm } from "./settings-xray-form";

export function ComponentSettingsDialog({
  open,
  component,
  flowId,
  flowLanguage,
  availableFields,
  clientCustomFields = [],
  onClose,
  hasXRayAccess = false,
  pageType = "global",
  portfolioCollectionId = null,
  pageVisibility = "internal",
  financialSituationModel = null,
  flowSupportsFollowup = false,
}: ComponentSettingsDialogProps) {
  // Use flow language for all UI in this dialog
  const t = pickT(T, flowLanguage);

  // QL-266 — the capacity section only renders when the model enables the
  // investment capacity calculator for at least one client type.
  const capacityConfiguredInModel = hasCapacityVariantEnabled(financialSituationModel?.settings);

  // QL-13 — the flow's quizzes, so a rule naming a deleted one is caught on
  // save rather than silently hiding content at runtime. While the list is
  // loading it is empty, which would read as "every quiz is gone", so the
  // check waits for it.
  const { quizzes: flowQuizzes, isUnknown: quizzesUnknown } = useFlowQuizzes(flowId);
  const { configuredAssessmentTypes, isUnknown: assessmentTypesUnknown } =
    useFlowConfiguredAssessmentTypes(flowId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sources this component itself produces (own flow field / produced
  // sentinels / QL-361: its own quiz, any product-assessment source) —
  // removed from the condition-source picker; a self-referential rule would
  // deadlock or trap-door the component. Same shared predicate, with the same
  // own ids, as the server guard that rejects it.
  const isConditionSourceExcluded = (sourceId: string) =>
    !!component &&
    isSelfReferentialConditionSource(
      component.componentType,
      sourceId,
      component.flowField?.id ?? component.flowFieldId,
      component.id,
    );

  // Component-specific settings
  const [textContent, setTextContent] = useState("");
  const [spacerHeight, setSpacerHeight] = useState(20);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [iframeUrl, setIframeUrl] = useState("");
  const [iframeHeight, setIframeHeight] = useState(String(IFRAME_HEIGHT_DEFAULT));
  const [iframeAspectRatio, setIframeAspectRatio] = useState<"" | IframeAspectRatio>("");
  const [imageUrl, setImageUrl] = useState("");
  const [embedHtml, setEmbedHtml] = useState("");
  const [modalButtonLabel, setModalButtonLabel] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState("");
  const [fileLabel, setFileLabel] = useState("");
  const [fileS3Key, setFileS3Key] = useState("");
  const [fileFileName, setFileFileName] = useState("");
  const [showPortfolioName, setShowPortfolioName] = useState(true);
  const [portfolioDisplayShowChart, setPortfolioDisplayShowChart] = useState(false);
  const [portfolioDisplayShowTable, setPortfolioDisplayShowTable] = useState(true);
  const [goalInputShowDescription, setGoalInputShowDescription] = useState(false);
  const [xrayDisplayMode, setXrayDisplayMode] = useState<string>("asset_allocation");
  const [xrayShowTable, setXrayShowTable] = useState(false);
  const [xrayShowChart, setXrayShowChart] = useState(true);
  const [xrayEnableFollowupComparison, setXrayEnableFollowupComparison] = useState(false);
  // QL-417 — Product Costs: new portfolio (default) or the followup's existing holdings.
  const [productCostsSource, setProductCostsSource] = useState<ProductCostsSource>("new");
  const [portfolioInputAllowedPortfolioTypes, setPortfolioInputAllowedPortfolioTypes] = useState<
    ("model" | "custom" | "dynamic")[]
  >(["model", "custom", "dynamic"]);
  // QL-697 — undefined = automatic (model when allowed, else the first allowed type).
  const [portfolioInputDefaultPortfolioType, setPortfolioInputDefaultPortfolioType] = useState<
    "model" | "custom" | "dynamic" | undefined
  >(undefined);
  const [portfolioInputShowPortfolioName, setPortfolioInputShowPortfolioName] = useState(true);
  const [portfolioInputShowDateInputs, setPortfolioInputShowDateInputs] = useState(false);
  const [costChartDisplayMode, setCostChartDisplayMode] = useState<"rolling" | "calendar">(
    "rolling",
  );
  const [costChartType, setCostChartType] = useState<"line" | "bar">("line");
  const [costChartProjectionYears, setCostChartProjectionYears] = useState(10);
  const [costTableShowHoldingPeriod, setCostTableShowHoldingPeriod] = useState(false);
  const [costTableHoldingPeriodYears, setCostTableHoldingPeriodYears] = useState(10);
  const [assetAllocShowChart, setAssetAllocShowChart] = useState(true);
  // QL-315 — asset class forecast
  const [acfShowChart, setAcfShowChart] = useState(true);
  const [acfShowTable, setAcfShowTable] = useState(true);
  const [acfEnabledHorizons, setAcfEnabledHorizons] = useState<AssetClassForecastHorizon[]>([
    ...ASSET_CLASS_FORECAST_HORIZONS,
  ]);
  const [acfDefaultHorizon, setAcfDefaultHorizon] = useState<AssetClassForecastHorizon>("10Y");
  // QL-314 — volatility chart
  const [vcEnabledHorizons, setVcEnabledHorizons] = useState<AssetClassForecastHorizon[]>([
    ...ASSET_CLASS_FORECAST_HORIZONS,
  ]);
  const [vcDefaultHorizon, setVcDefaultHorizon] = useState<AssetClassForecastHorizon>("1Y");
  const [vcShowTable, setVcShowTable] = useState(false);
  // QL-313 — equity share chart
  const [escEnabledHorizons, setEscEnabledHorizons] = useState<AssetClassForecastHorizon[]>([
    ...ASSET_CLASS_FORECAST_HORIZONS,
  ]);
  const [escDefaultHorizon, setEscDefaultHorizon] = useState<AssetClassForecastHorizon>("1Y");
  const [escShowTable, setEscShowTable] = useState(true);
  const [assetAllocShowTable, setAssetAllocShowTable] = useState(false);
  const [assetAllocEnableFollowupComparison, setAssetAllocEnableFollowupComparison] =
    useState(false);
  const [expectedValueShowChart, setExpectedValueShowChart] = useState(true);
  const [expectedValueShowSummaryTable, setExpectedValueShowSummaryTable] = useState(false);
  const [expectedValueShowYearlyTable, setExpectedValueShowYearlyTable] = useState(false);
  const [expectedValueCompareWithBank, setExpectedValueCompareWithBank] = useState(false);
  const [forecastShowStatistics, setForecastShowStatistics] = useState(true);
  const [forecastShowTable, setForecastShowTable] = useState(true);
  const [forecastCompareWithBank, setForecastCompareWithBank] = useState(false);
  // Product Assessment / Client Classification settings
  const [componentStyle, setComponentStyle] = useState<ComponentStyleType>("none");
  const [componentTitle, setComponentTitle] = useState("");
  const [componentDescription, setComponentDescription] = useState("");

  // Custom Portfolio field visibility settings
  const [customPortfolioShowIsin, setCustomPortfolioShowIsin] = useState(false);
  const [customPortfolioShowProductType, setCustomPortfolioShowProductType] = useState(false);
  const [customPortfolioOnlyAllowSuitable, setCustomPortfolioOnlyAllowSuitable] = useState(false);
  // QP-179 — Sustainability status: whether the advisor can accept a deviation.
  const [sustainabilityStatusAllowOverride, setSustainabilityStatusAllowOverride] = useState(true);
  // QL-281 — Goal list: show the description textarea in the add/edit modal
  const [goalListShowDescription, setGoalListShowDescription] = useState(false);

  // QL-13 — the quiz is authored here, in the component that runs it. There is
  // no quiz entity to point at: this component's id is the quiz id everywhere.
  const [quizSettings, setQuizSettings] = useState<QuizSettings>(EMPTY_QUIZ_SETTINGS);
  // QP-172 — Portfolio list toggle columns
  const [portfolioListShowAmount, setPortfolioListShowAmount] = useState(true);
  const [portfolioListShowFrequency, setPortfolioListShowFrequency] = useState(true);
  const [portfolioListShowDates, setPortfolioListShowDates] = useState(true);
  // Financial Situation settings
  const [finSitShowIncome, setFinSitShowIncome] = useState(true);
  const [finSitShowAssets, setFinSitShowAssets] = useState(true);
  const [finSitShowLiabilities, setFinSitShowLiabilities] = useState(true);
  const [finSitShowCommitments, setFinSitShowCommitments] = useState(true);
  const [finSitShowCapacitySection, setFinSitShowCapacitySection] = useState(true);
  const [finSitShowStability, setFinSitShowStability] = useState(true);
  const [finSitShowExpectingChanges, setFinSitShowExpectingChanges] = useState(true);
  const [finSitShowCapacity, setFinSitShowCapacity] = useState(true);
  const [finSitShowSummary, setFinSitShowSummary] = useState(true);
  // Sumsub settings
  const [sumsubLivenessVerification, setSumsubLivenessVerification] = useState(true);

  // Conditional display state
  const [conditionalEnabled, setConditionalEnabled] = useState(false);
  const [sourceFieldId, setSourceFieldId] = useState("");
  const [operator, setOperator] = useState<ConditionalOperator>("equals");
  const [conditionValue, setConditionValue] = useState("");

  // Clear conditionValue when sourceFieldId changes (different fields have different valid values)
  const handleSourceFieldChange = (newFieldId: string) => {
    setSourceFieldId(newFieldId);
    setConditionValue("");
  };

  useEffect(() => {
    if (open && component) {
      // Parse existing config
      try {
        const config = component.config ? JSON.parse(component.config) : {};

        // Load component-specific settings
        switch (component.componentType) {
          case "text_block": {
            // A legacy `title` (QL-296 first pass) is folded into the content as
            // its heading, so the next save stores it the current way.
            setTextContent(textBlockContent(config as TextBlockConfig));
            break;
          }

          case "spacer": {
            const spacerConfig = config as SpacerConfig;
            setSpacerHeight(spacerConfig.height || 20);
            break;
          }

          case "portfolio_display": {
            const portfolioConfig = config as PortfolioDisplayConfig;
            setShowPortfolioName(portfolioConfig.showPortfolioName !== false);
            setPortfolioDisplayShowChart(portfolioConfig.showChart === true);
            setPortfolioDisplayShowTable(portfolioConfig.showTable !== false);
            setComponentStyle(portfolioConfig.style || "none");
            setComponentTitle(portfolioConfig.title || "");
            setComponentDescription(portfolioConfig.description || "");
            break;
          }

          case "portfolio_xray": {
            const xrayConfig = config as PortfolioXrayConfig;
            setXrayDisplayMode(xrayConfig.displayMode || "asset_allocation");
            setXrayShowTable(xrayConfig.showTable || false);
            setXrayShowChart(xrayConfig.showChart !== false); // Default to true
            setXrayEnableFollowupComparison(xrayConfig.enableFollowupComparison === true); // Default to false
            setComponentStyle(xrayConfig.style || "none");
            setComponentTitle(xrayConfig.title || "");
            setComponentDescription(xrayConfig.description || "");
            break;
          }

          case "portfolio_forecast": {
            const forecastConfig = config as PortfolioForecastConfig;
            setComponentStyle(forecastConfig.style || "none");
            setComponentTitle(forecastConfig.title || "");
            setComponentDescription(forecastConfig.description || "");
            setForecastShowStatistics(forecastConfig.showStatistics !== false); // Default to true
            setForecastShowTable(forecastConfig.showTable !== false); // Default to true
            setForecastCompareWithBank(forecastConfig.compareWithBank === true); // Default to false
            break;
          }

          case "youtube_video": {
            const youtubeConfig = config as YoutubeVideoConfig;
            setYoutubeUrl(youtubeConfig.url || "");
            break;
          }

          case "iframe": {
            const iframeConfig = config as IframeConfig;
            setIframeUrl(iframeConfig.url || "");
            setIframeHeight(String(iframeConfig.height ?? IFRAME_HEIGHT_DEFAULT));
            setIframeAspectRatio(iframeConfig.aspectRatio ?? "");
            setComponentStyle(iframeConfig.style || "none");
            setComponentTitle(iframeConfig.title || "");
            setComponentDescription(iframeConfig.description || "");
            break;
          }

          case "image": {
            const imageConfig = config as ImageConfig;
            setImageUrl(imageConfig.url || "");
            break;
          }

          case "embed": {
            const embedConfig = config as EmbedConfig;
            setEmbedHtml(embedConfig.html || "");
            setComponentStyle(embedConfig.style || "none");
            setComponentTitle(embedConfig.title || "");
            setComponentDescription(embedConfig.description || "");
            break;
          }

          case "modal": {
            const modalConfig = config as ModalConfig;
            setModalButtonLabel(modalConfig.buttonLabel || "");
            setModalTitle(modalConfig.title || "");
            setModalContent(modalConfig.content || "");
            break;
          }

          case "file": {
            const fileConfig = config as FileConfig;
            setFileLabel(fileConfig.label || "");
            setFileS3Key(fileConfig.s3Key || "");
            setFileFileName(fileConfig.fileName || "");
            break;
          }

          case "goal_input": {
            // goal_input edits goal-level fields (QP-150) — name/type + optional description (QP-187).
            const goalInputConfig = config as GoalInputConfig;
            setComponentStyle(goalInputConfig.style || "none");
            setComponentTitle(goalInputConfig.title || "");
            setComponentDescription(goalInputConfig.description || "");
            setGoalInputShowDescription(goalInputConfig.showDescription === true);
            break;
          }

          case "portfolio_input": {
            const portfolioInputConfig = config as PortfolioInputConfig;
            setPortfolioInputAllowedPortfolioTypes(
              portfolioInputConfig.allowedPortfolioTypes || ["model", "custom", "dynamic"],
            );
            setPortfolioInputDefaultPortfolioType(portfolioInputConfig.defaultPortfolioType);
            setPortfolioInputShowPortfolioName(portfolioInputConfig.showPortfolioName !== false);
            setPortfolioInputShowDateInputs(portfolioInputConfig.showDateInputs || false);
            setComponentStyle(portfolioInputConfig.style || "none");
            setComponentTitle(portfolioInputConfig.title || "");
            setComponentDescription(portfolioInputConfig.description || "");
            break;
          }

          case "risk_assessment": {
            const raConfig = config as RiskAssessmentConfig;
            setComponentStyle(raConfig.style || "none");
            setComponentTitle(raConfig.title || "");
            setComponentDescription(raConfig.description || "");
            break;
          }

          case "cost_chart": {
            const costChartConfig = config as CostChartConfig;
            setCostChartDisplayMode(costChartConfig.displayMode || "rolling");
            setCostChartType(costChartConfig.chartType || "line");
            setCostChartProjectionYears(costChartConfig.projectionYears || 10);
            setComponentStyle(costChartConfig.style || "none");
            setComponentTitle(costChartConfig.title || "");
            setComponentDescription(costChartConfig.description || "");
            break;
          }

          case "cost_table": {
            const costTableConfig = config as CostTableConfig;
            setCostTableShowHoldingPeriod(costTableConfig.showHoldingPeriod || false);
            setCostTableHoldingPeriodYears(costTableConfig.holdingPeriodYears || 10);
            setComponentStyle(costTableConfig.style || "none");
            setComponentTitle(costTableConfig.title || "");
            setComponentDescription(costTableConfig.description || "");
            break;
          }

          case "portfolio_costs": {
            const portfolioCostsConfig = config as PortfolioCostsConfig;
            setComponentStyle(portfolioCostsConfig.style || "none");
            setComponentTitle(portfolioCostsConfig.title || "");
            setComponentDescription(portfolioCostsConfig.description || "");
            break;
          }

          case "product_costs": {
            const productCostsConfig = config as ProductCostsConfig;
            setComponentStyle(productCostsConfig.style || "none");
            setComponentTitle(productCostsConfig.title || "");
            setComponentDescription(productCostsConfig.description || "");
            setProductCostsSource(
              productCostsConfig.portfolioSource === "existing" ? "existing" : "new",
            );
            break;
          }

          case "account_cash_overview":
          case "cash_transfers":
          // QL-375 — title / description / style only.
          case "mandate":
          case "mandate_comparison":
            setComponentStyle(config.style || "none");
            setComponentTitle(config.title || "");
            setComponentDescription(config.description || "");
            break;

          case "cost_benefit": {
            const costBenefitConfig = config as CostBenefitConfig;
            setComponentStyle(costBenefitConfig.style || "none");
            setComponentTitle(costBenefitConfig.title || "");
            setComponentDescription(costBenefitConfig.description || "");
            break;
          }

          case "asset_allocation": {
            const assetAllocConfig = config as AssetAllocationConfig;
            setAssetAllocShowChart(assetAllocConfig.showChart !== false); // Default to true
            setAssetAllocShowTable(assetAllocConfig.showTable || false);
            setAssetAllocEnableFollowupComparison(
              assetAllocConfig.enableFollowupComparison === true,
            ); // Default to false
            setComponentStyle(assetAllocConfig.style || "none");
            setComponentTitle(assetAllocConfig.title || "");
            setComponentDescription(assetAllocConfig.description || "");
            break;
          }

          case "asset_class_forecast": {
            const acfConfig = config as AssetClassForecastConfig;
            setAcfShowChart(acfConfig.showChart !== false); // Default to true
            setAcfShowTable(acfConfig.showTable !== false); // Default to true
            setAcfEnabledHorizons(
              acfConfig.enabledHorizons?.length
                ? acfConfig.enabledHorizons
                : [...ASSET_CLASS_FORECAST_HORIZONS],
            );
            setAcfDefaultHorizon(acfConfig.defaultHorizon || "10Y");
            setComponentStyle(acfConfig.style || "none");
            setComponentTitle(acfConfig.title || "");
            setComponentDescription(acfConfig.description || "");
            break;
          }
          case "volatility_chart": {
            const vcConfig = config as VolatilityChartConfig;
            setVcEnabledHorizons(
              vcConfig.enabledHorizons?.length
                ? vcConfig.enabledHorizons
                : [...ASSET_CLASS_FORECAST_HORIZONS],
            );
            setVcDefaultHorizon(vcConfig.defaultHorizon || "1Y");
            setVcShowTable(vcConfig.showTable === true);
            setComponentStyle(vcConfig.style || "none");
            setComponentTitle(vcConfig.title || "");
            setComponentDescription(vcConfig.description || "");
            break;
          }
          case "equity_share_chart": {
            const escConfig = config as EquityShareChartConfig;
            setEscEnabledHorizons(
              escConfig.enabledHorizons?.length
                ? escConfig.enabledHorizons
                : [...ASSET_CLASS_FORECAST_HORIZONS],
            );
            setEscDefaultHorizon(escConfig.defaultHorizon || "1Y");
            setEscShowTable(escConfig.showTable !== false); // Default to true
            setComponentStyle(escConfig.style || "none");
            setComponentTitle(escConfig.title || "");
            setComponentDescription(escConfig.description || "");
            break;
          }

          case "expected_value": {
            const expectedValueConfig = config as ExpectedValueConfig;
            setComponentStyle(expectedValueConfig.style || "none");
            setComponentTitle(expectedValueConfig.title || "");
            setComponentDescription(expectedValueConfig.description || "");
            setExpectedValueShowChart(expectedValueConfig.showChart !== false); // Default to true
            setExpectedValueShowSummaryTable(expectedValueConfig.showSummaryTable || false);
            setExpectedValueShowYearlyTable(expectedValueConfig.showYearlyTable || false);
            setExpectedValueCompareWithBank(expectedValueConfig.compareWithBank === true); // Default to false
            break;
          }

          case "historical_return": {
            const hrConfig = config as HistoricalReturnConfig;
            setComponentStyle(hrConfig.style || "none");
            setComponentTitle(hrConfig.title || "");
            setComponentDescription(hrConfig.description || "");
            break;
          }

          case "product_assessment": {
            const peConfig = config as ProductAssessmentConfig;
            setComponentStyle(peConfig.style || "none");
            setComponentTitle(peConfig.title || "");
            setComponentDescription(peConfig.description || "");
            break;
          }

          case "client_classification": {
            const ccConfig = config as ClientClassificationConfig;
            setComponentStyle(ccConfig.style || "none");
            setComponentTitle(ccConfig.title || "");
            setComponentDescription(ccConfig.description || "");
            break;
          }

          case "custom_portfolio":
            setComponentStyle(config.style || "none");
            setComponentTitle(config.title || "");
            setComponentDescription(config.description || "");
            setCustomPortfolioShowIsin(config.showIsin || false);
            setCustomPortfolioShowProductType(config.showProductType || false);
            setCustomPortfolioOnlyAllowSuitable(config.onlyAllowSuitableProducts || false);
            break;

          case "wealth_plan":
            setComponentStyle(config.style || "none");
            setComponentTitle(config.title || "");
            setComponentDescription(config.description || "");
            break;

          case "goal_list":
            setComponentStyle(config.style || "none");
            setComponentTitle(config.title || "");
            setComponentDescription(config.description || "");
            setGoalListShowDescription(config.showDescription === true);
            break;

          case "financial_situation": {
            const finSitConfig = config as FinancialSituationConfig;
            setComponentStyle(finSitConfig.style || "none");
            setComponentTitle(finSitConfig.title || "");
            setComponentDescription(finSitConfig.description || "");
            // Section visibility
            setFinSitShowIncome(finSitConfig.showIncome !== false);
            setFinSitShowAssets(finSitConfig.showAssets !== false);
            setFinSitShowLiabilities(finSitConfig.showLiabilities !== false);
            setFinSitShowCommitments(finSitConfig.showCommitments !== false);
            setFinSitShowCapacitySection(finSitConfig.showCapacity !== false);
            // Display options
            setFinSitShowStability(finSitConfig.showStabilityQuestion !== false);
            setFinSitShowExpectingChanges(finSitConfig.showExpectingChangesQuestion !== false);
            setFinSitShowCapacity(finSitConfig.showCapacityClassification !== false);
            setFinSitShowSummary(finSitConfig.showSummaryMetrics !== false);
            break;
          }

          case "trapets_lookup":
            setComponentStyle(config.style || "none");
            setComponentTitle(config.title || "");
            setComponentDescription(config.description || "");
            break;

          case "sumsub_verification":
            setComponentStyle(config.style || "none");
            setComponentTitle(config.title || "");
            setComponentDescription(config.description || "");
            setSumsubLivenessVerification(config.livenessVerification ?? true);
            break;

          case "enin_lookup":
            setComponentStyle(config.style || "none");
            setComponentTitle(config.title || "");
            setComponentDescription(config.description || "");
            break;

          case "sustainability_status":
            setComponentStyle(config.style || "none");
            setComponentTitle(config.title || "");
            setComponentDescription(config.description || "");
            // Default TRUE — only an explicit `false` disables overrides.
            setSustainabilityStatusAllowOverride(config.allowOverride !== false);
            break;

          case "quiz":
            setComponentStyle(config.style || "none");
            setComponentTitle(config.title || "");
            setComponentDescription(config.description || "");
            setQuizSettings({
              questions: config.questions ?? [],
              minScore: config.minScore ?? 1,
              questionsToAsk: config.questionsToAsk ?? Math.max(config.questions?.length ?? 1, 1),
              maxAttempts: config.maxAttempts ?? null,
              attemptResetDays: config.attemptResetDays ?? null,
            });
            break;

          case "portfolio_list":
            setComponentStyle(config.style || "none");
            setComponentTitle(config.title || "");
            setComponentDescription(config.description || "");
            setPortfolioListShowAmount(config.showAmount !== false);
            setPortfolioListShowFrequency(config.showFrequency !== false);
            setPortfolioListShowDates(config.showDates !== false);
            break;
        }

        // Load conditional display settings
        const conditional = config?.conditionalDisplay;
        if (conditional) {
          setConditionalEnabled(conditional.enabled || false);
          setSourceFieldId(conditional.sourceFieldId || "");
          setOperator(conditional.operator || "equals");
          setConditionValue(conditional.value || "");
        } else {
          setConditionalEnabled(false);
          setSourceFieldId("");
          setOperator("equals");
          setConditionValue("");
        }
      } catch (err) {
        // Invalid config, use defaults
        log.error({ err }, "failed to parse component config");
        resetToDefaults();
      }
      setError(null);

      setLoading(false); // Reset loading state when dialog opens
    }
  }, [open, component]);

  const resetToDefaults = () => {
    setTextContent("");
    setSpacerHeight(20);
    setShowPortfolioName(true);
    setXrayDisplayMode("asset_allocation");
    setXrayShowTable(false);
    setXrayShowChart(true);
    setYoutubeUrl("");
    setIframeUrl("");
    setIframeHeight(String(IFRAME_HEIGHT_DEFAULT));
    setIframeAspectRatio("");
    setImageUrl("");
    setEmbedHtml("");
    setPortfolioInputAllowedPortfolioTypes(["model", "custom", "dynamic"]);
    setPortfolioInputDefaultPortfolioType(undefined);
    setCostChartDisplayMode("rolling");
    setAssetAllocShowChart(true);
    setAssetAllocShowTable(false);
    setForecastShowStatistics(true);
    setForecastShowTable(true);
    setProductCostsSource("new");
    setComponentStyle("none");
    setComponentTitle("");
    setComponentDescription("");
    setCustomPortfolioShowIsin(false);
    setCustomPortfolioShowProductType(false);
    setSustainabilityStatusAllowOverride(true);
    setPortfolioListShowAmount(true);
    setPortfolioListShowFrequency(true);
    setPortfolioListShowDates(true);
    setFinSitShowIncome(true);
    setFinSitShowAssets(true);
    setFinSitShowLiabilities(true);
    setFinSitShowCommitments(true);
    setFinSitShowStability(true);
    setFinSitShowCapacitySection(true);
    setFinSitShowExpectingChanges(true);
    setFinSitShowCapacity(true);
    setFinSitShowSummary(true);
    setSumsubLivenessVerification(true);
    setVcEnabledHorizons([...ASSET_CLASS_FORECAST_HORIZONS]);
    setVcDefaultHorizon("1Y");
    setVcShowTable(false);
    // QL-13 — the dialog stays mounted between opens, so a quiz left in state
    // from an earlier component would be saved onto this one the moment its
    // own config failed to parse.
    setQuizSettings(EMPTY_QUIZ_SETTINGS);
    setConditionalEnabled(false);
    setSourceFieldId("");
    setOperator("equals");
    setConditionValue("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!component) return;

    setError(null);

    // Validation
    const ruleProblem = conditionalEnabled
      ? findConditionRuleProblem(
          { sourceFieldId, operator, value: conditionValue },
          {
            quizzes: quizzesUnknown ? null : flowQuizzes,
            assessmentTypes: assessmentTypesUnknown ? null : configuredAssessmentTypes,
          },
        )
      : null;
    if (ruleProblem) {
      setError(conditionRuleProblemMessage(ruleProblem, flowLanguage));
      return;
    }

    // Component-specific validation
    if (component.componentType === "text_block" && !textContent.trim()) {
      setError(t.textContentRequired);
      return;
    }

    if (component.componentType === "spacer" && spacerHeight < 0) {
      setError(t.spacerHeightPositive);
      return;
    }

    if (component.componentType === "youtube_video" && !youtubeUrl.trim()) {
      setError(t.youtubeUrlRequired);
      return;
    }

    if (component.componentType === "iframe") {
      if (iframeUrlProblem(iframeUrl.trim()) !== null) {
        setError(t.iframeUrlInvalid);
        return;
      }
      if (isAppOrigin(iframeUrl.trim())) {
        setError(t.iframeUrlAppOrigin);
        return;
      }
      if (!iframeAspectRatio && !isValidIframeHeight(Number(iframeHeight))) {
        setError(t.iframeHeightInvalid);
        return;
      }
    }

    if (component.componentType === "image" && !imageUrl.trim()) {
      setError(t.imageRequired);
      return;
    }

    if (component.componentType === "embed" && !embedHtml.trim()) {
      setError(t.embedHtmlRequired);
      return;
    }

    if (component.componentType === "modal") {
      if (!modalButtonLabel.trim()) {
        setError(t.modalButtonLabelRequired);
        return;
      }
      if (!modalTitle.trim()) {
        setError(t.modalTitleRequired);
        return;
      }
      if (!modalContent.trim()) {
        setError(t.modalContentRequired);
        return;
      }
    }

    if (component.componentType === "file" && !fileS3Key) {
      setError(t.fileDocumentRequired);
      return;
    }

    // X-ray validation: at least one of showChart or showTable must be selected (except for top_holdings and equity_style)
    if (
      component.componentType === "portfolio_xray" &&
      xrayDisplayMode !== "top_holdings" &&
      xrayDisplayMode !== "equity_style" &&
      !xrayShowChart &&
      !xrayShowTable
    ) {
      setError(t.selectAtLeastOneDisplay);
      return;
    }

    if (
      component.componentType === "portfolio_display" &&
      !portfolioDisplayShowChart &&
      !portfolioDisplayShowTable
    ) {
      setError(t.selectAtLeastOneDisplay);
      return;
    }

    // Asset allocation validation: at least one display option must be selected
    if (
      component.componentType === "asset_allocation" &&
      !assetAllocShowChart &&
      !assetAllocShowTable
    ) {
      setError(t.selectAtLeastOneDisplay);
      return;
    }

    // Expected value validation: at least one display option must be selected
    if (
      component.componentType === "expected_value" &&
      !expectedValueShowChart &&
      !expectedValueShowSummaryTable &&
      !expectedValueShowYearlyTable
    ) {
      setError(t.selectAtLeastOneDisplayOption);
      return;
    }

    // Goal input validation: at least one portfolio type must be allowed
    if (
      component.componentType === "portfolio_input" &&
      portfolioInputAllowedPortfolioTypes.length === 0
    ) {
      setError(t.selectAtLeastOnePortfolioType);
      return;
    }

    // QL-13 — same `validateQuizConfig` the server runs, so the form cannot
    // offer a quiz the API will reject.
    if (component.componentType === "quiz") {
      const quizError = quizSettingsError(quizSettings, flowLanguage);
      if (quizError) {
        setError(quizError);
        return;
      }
    }

    // QL-313 — the horizon-chart settings block allows an empty Time Ranges
    // selection with a warning; the save refuses it.
    if (
      (component?.componentType === "asset_class_forecast" && acfEnabledHorizons.length === 0) ||
      (component?.componentType === "volatility_chart" && vcEnabledHorizons.length === 0) ||
      (component?.componentType === "equity_share_chart" && escEnabledHorizons.length === 0)
    ) {
      setError(t.pleaseSelectTimeRange);
      return;
    }

    setLoading(true);

    try {
      // Build conditional display config
      const conditionalDisplay = conditionalEnabled
        ? {
            enabled: true,
            sourceFieldId,
            operator,
            value: conditionValue,
          }
        : {
            enabled: false,
            sourceFieldId: "",
            operator: "equals" as ConditionalOperator,
          };

      // Build component-specific config
      let newConfig: ComponentConfig;

      switch (component.componentType) {
        case "risk_assessment":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            conditionalDisplay,
          } as RiskAssessmentConfig;
          break;

        case "portfolio_display":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            showChart: portfolioDisplayShowChart,
            showTable: portfolioDisplayShowTable,
            showPortfolioName,
            conditionalDisplay,
          } as PortfolioDisplayConfig;
          break;

        case "portfolio_xray":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            displayMode: xrayDisplayMode as any,
            showTable: xrayShowTable,
            showChart: xrayShowChart,
            enableFollowupComparison: xrayEnableFollowupComparison,
            conditionalDisplay,
          } as PortfolioXrayConfig;
          break;

        case "portfolio_forecast":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            showStatistics: forecastShowStatistics,
            showTable: forecastShowTable,
            compareWithBank: forecastCompareWithBank,
            conditionalDisplay,
          } as PortfolioForecastConfig;
          break;

        case "cost_table":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            showHoldingPeriod: costTableShowHoldingPeriod,
            holdingPeriodYears: costTableHoldingPeriodYears,
            conditionalDisplay,
          } as CostTableConfig;
          break;

        case "cost_chart":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            displayMode: costChartDisplayMode,
            chartType: costChartType,
            projectionYears: costChartProjectionYears,
            conditionalDisplay,
          } as CostChartConfig;
          break;

        case "portfolio_costs":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            conditionalDisplay,
          } as PortfolioCostsConfig;
          break;

        case "product_costs":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            conditionalDisplay,
            portfolioSource: productCostsSource,
          } as ProductCostsConfig;
          break;

        case "account_cash_overview":
        case "cash_transfers":
        // QL-375 — title / description / style only.
        case "mandate":
        case "mandate_comparison":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            conditionalDisplay,
          };
          break;

        case "cost_benefit":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            conditionalDisplay,
          } as CostBenefitConfig;
          break;

        case "expected_value":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            showChart: expectedValueShowChart,
            showSummaryTable: expectedValueShowSummaryTable,
            showYearlyTable: expectedValueShowYearlyTable,
            compareWithBank: expectedValueCompareWithBank,
            conditionalDisplay,
          } as ExpectedValueConfig;
          break;

        case "historical_return":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            conditionalDisplay,
          } as HistoricalReturnConfig;
          break;

        case "text_block":
          newConfig = {
            content: textContent,
            conditionalDisplay,
          } as TextBlockConfig;
          break;

        case "spacer":
          newConfig = {
            height: spacerHeight,
            conditionalDisplay,
          } as SpacerConfig;
          break;

        case "youtube_video":
          newConfig = {
            url: youtubeUrl,
            conditionalDisplay,
          } as YoutubeVideoConfig;
          break;

        case "iframe":
          newConfig = {
            url: iframeUrl.trim(),
            ...(iframeAspectRatio
              ? { aspectRatio: iframeAspectRatio }
              : { height: Number(iframeHeight) }),
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            conditionalDisplay,
          } as IframeConfig;
          break;

        case "image":
          newConfig = {
            url: imageUrl,
            conditionalDisplay,
          } as ImageConfig;
          break;

        case "embed":
          newConfig = {
            html: embedHtml,
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            conditionalDisplay,
          } as EmbedConfig;
          break;

        case "modal":
          newConfig = {
            buttonLabel: modalButtonLabel.trim(),
            title: modalTitle.trim(),
            content: modalContent,
            conditionalDisplay,
          } as ModalConfig;
          break;

        case "file":
          newConfig = {
            label: fileLabel.trim() || undefined,
            s3Key: fileS3Key,
            fileName: fileFileName,
            conditionalDisplay,
          } as FileConfig;
          break;

        case "goal_input":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            showDescription: goalInputShowDescription,
            conditionalDisplay,
          } as GoalInputConfig;
          break;

        case "portfolio_input":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            showPortfolioType: portfolioInputAllowedPortfolioTypes.length > 0, // Show if there are any types to choose from
            allowedPortfolioTypes: portfolioInputAllowedPortfolioTypes,
            defaultPortfolioType: portfolioInputDefaultPortfolioType,
            showPortfolioName: portfolioInputShowPortfolioName,
            showDateInputs: portfolioInputShowDateInputs,
            conditionalDisplay,
          } as PortfolioInputConfig;
          break;

        case "custom_portfolio":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            showIsin: customPortfolioShowIsin,
            showProductType: customPortfolioShowProductType,
            onlyAllowSuitableProducts: customPortfolioOnlyAllowSuitable,
            conditionalDisplay,
          };
          break;

        case "asset_allocation":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            showChart: assetAllocShowChart,
            showTable: assetAllocShowTable,
            enableFollowupComparison: assetAllocEnableFollowupComparison,
            conditionalDisplay,
          } as AssetAllocationConfig;
          break;

        case "asset_class_forecast":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            showChart: acfShowChart,
            showTable: acfShowTable,
            enabledHorizons: acfEnabledHorizons,
            defaultHorizon: acfDefaultHorizon,
            conditionalDisplay,
          } as AssetClassForecastConfig;
          break;

        case "volatility_chart":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            enabledHorizons: vcEnabledHorizons,
            defaultHorizon: vcDefaultHorizon,
            showTable: vcShowTable,
            conditionalDisplay,
          } as VolatilityChartConfig;
          break;

        case "equity_share_chart":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            enabledHorizons: escEnabledHorizons,
            defaultHorizon: escDefaultHorizon,
            showTable: escShowTable,
            conditionalDisplay,
          } as EquityShareChartConfig;
          break;

        case "product_assessment":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            conditionalDisplay,
          } as ProductAssessmentConfig;
          break;

        case "quiz":
          newConfig = {
            ...quizSettings,
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            conditionalDisplay,
          } as QuizConfig;
          break;

        case "client_classification":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            conditionalDisplay,
          } as ClientClassificationConfig;
          break;

        case "wealth_plan":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            conditionalDisplay,
          };
          break;

        case "goal_list":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            showDescription: goalListShowDescription,
            conditionalDisplay,
          };
          break;

        case "financial_situation": {
          // Preserve existing field definitions when updating
          const existingConfig = component.config ? JSON.parse(component.config) : {};
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            incomeFields: existingConfig.incomeFields,
            assetFields: existingConfig.assetFields,
            liabilityFields: existingConfig.liabilityFields,
            commitmentFields: existingConfig.commitmentFields,
            // Section visibility
            showIncome: finSitShowIncome,
            showAssets: finSitShowAssets,
            showLiabilities: finSitShowLiabilities,
            showCommitments: finSitShowCommitments,
            showCapacity: finSitShowCapacitySection,
            // Display options
            showStabilityQuestion: finSitShowStability,
            showExpectingChangesQuestion: finSitShowExpectingChanges,
            showCapacityClassification: finSitShowCapacity,
            showSummaryMetrics: finSitShowSummary,
            conditionalDisplay,
          } as FinancialSituationConfig;
          break;
        }

        case "trapets_lookup":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            conditionalDisplay,
          };
          break;

        case "sumsub_verification":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            livenessVerification: sumsubLivenessVerification,
            conditionalDisplay,
          };
          break;

        case "enin_lookup":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            conditionalDisplay,
          };
          break;

        case "sustainability_status":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            allowOverride: sustainabilityStatusAllowOverride,
            conditionalDisplay,
          };
          break;

        case "portfolio_list":
          newConfig = {
            style: componentStyle,
            title: componentTitle.trim() || undefined,
            description: componentDescription.trim() || undefined,
            showAmount: portfolioListShowAmount,
            showFrequency: portfolioListShowFrequency,
            showDates: portfolioListShowDates,
            conditionalDisplay,
          };
          break;

        default:
          newConfig = {
            conditionalDisplay,
          };
          break;
      }

      await patch(`/internal/flows/${flowId}/components/${component.id}`, { config: newConfig });
      onClose(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.errorOccurred);
    } finally {
      setLoading(false);
    }
  };

  const getComponentLabel = () => {
    if (!component) return t.componentPrefix.replace(":", "");
    if (component.componentType === "field" && component.flowField) {
      return `${t.fieldPrefix} ${component.flowField.name}`;
    }
    return `${t.componentPrefix} ${component.componentType.replace("_", " ")}`;
  };

  const hasComponentSettings =
    component &&
    [
      "text_block",
      "spacer",
      "portfolio_display",
      "portfolio_xray",
      "portfolio_forecast",
      "youtube_video",
      "iframe",
      "image",
      "embed",
      "modal",
      "file",
      "goal_input",
      "portfolio_input",
      "custom_portfolio",
      "risk_assessment",
      "cost_table",
      "cost_chart",
      "portfolio_costs",
      "product_costs",
      "cost_benefit",
      "asset_allocation",
      "asset_class_forecast",
      "volatility_chart",
      "equity_share_chart",
      "mandate",
      "mandate_comparison",
      "expected_value",
      "historical_return",
      "product_assessment",
      "client_classification",
      "wealth_plan",
      "financial_situation",
      "trapets_lookup",
      "sumsub_verification",
      "enin_lookup",
      "sustainability_status",
      "portfolio_list",
      "goal_list",
      "account_cash_overview",
      "cash_transfers",
      // QL-13 — the quiz picker lives here, so the gear icon must open.
      "quiz",
    ].includes(component.componentType);

  // Components that show style settings
  const hasStyleSettings =
    component &&
    [
      "iframe",
      "product_assessment",
      "client_classification",
      "risk_assessment",
      "goal_input",
      "portfolio_input",
      "custom_portfolio",
      "portfolio_display",
      "portfolio_xray",
      "portfolio_forecast",
      "asset_allocation",
      "asset_class_forecast",
      "volatility_chart",
      "equity_share_chart",
      "mandate",
      "mandate_comparison",
      "expected_value",
      "historical_return",
      "cost_table",
      "cost_chart",
      "portfolio_costs",
      "product_costs",
      "cost_benefit",
      "wealth_plan",
      "financial_situation",
      "trapets_lookup",
      "sumsub_verification",
      "enin_lookup",
      "sustainability_status",
      "portfolio_list",
      "goal_list",
      "account_cash_overview",
      "cash_transfers",
      "quiz",
      "embed",
    ].includes(component.componentType);

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogScrollContent className="max-w-3xl">
        <DialogScrollHeader>
          <DialogTitle>{t.dialogTitle}</DialogTitle>
          <DialogDescription>{t.dialogDescription}</DialogDescription>
        </DialogScrollHeader>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col min-h-0">
          <DialogBody className="space-y-6">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            {/* Component Info */}
            <div className="p-3 bg-muted rounded-md">
              <div className="text-sm font-medium">{getComponentLabel()}</div>
            </div>

            {hasComponentSettings ? (
              <Tabs defaultValue="component" className="w-full">
                <TabsList
                  className={`grid w-full ${
                    component?.componentType === "custom_portfolio" ||
                    component?.componentType === "embed"
                      ? "grid-cols-3"
                      : "grid-cols-2"
                  }`}
                >
                  <TabsTrigger value="component">{t.componentSettingsTab}</TabsTrigger>
                  {component?.componentType === "custom_portfolio" && (
                    <TabsTrigger value="fields">{t.fieldsTab}</TabsTrigger>
                  )}
                  {component?.componentType === "embed" && (
                    <TabsTrigger value="html">{t.htmlTab}</TabsTrigger>
                  )}
                  <TabsTrigger value="conditional">{t.conditionalDisplayTab}</TabsTrigger>
                </TabsList>

                <TabsContent value="component" className="space-y-4 mt-4">
                  {/* Text Block Settings — QL-296: one Content field, the heading
                      lives inside it (single heading style + paragraphs). */}
                  {component?.componentType === "text_block" && (
                    <div className="space-y-2">
                      <Label htmlFor="text-content">
                        {t.content} <span className="text-destructive">{t.required}</span>
                      </Label>
                      <MarkdownEditor
                        value={textContent}
                        onChange={setTextContent}
                        placeholder={t.textBlockPlaceholder}
                        showAI={false}
                        headings="single"
                      />
                      <p className="text-xs text-muted-foreground">{t.textBlockHelp}</p>
                    </div>
                  )}

                  {/* Spacer Settings */}
                  {component?.componentType === "spacer" && (
                    <div className="space-y-2">
                      <Label htmlFor="spacer-height">
                        {t.heightPixels} <span className="text-destructive">{t.required}</span>
                      </Label>
                      <Input
                        id="spacer-height"
                        type="number"
                        value={spacerHeight}
                        onChange={(e) => setSpacerHeight(Number(e.target.value))}
                        min={0}
                        max={500}
                        required
                      />
                      <p className="text-xs text-muted-foreground">{t.spacerHelp}</p>
                    </div>
                  )}

                  {/* Portfolio Display Settings */}
                  {component?.componentType === "portfolio_display" && (
                    <SettingsPortfolioDisplayForm
                      flowLanguage={flowLanguage}
                      showChart={portfolioDisplayShowChart}
                      onShowChartChange={setPortfolioDisplayShowChart}
                      showTable={portfolioDisplayShowTable}
                      onShowTableChange={setPortfolioDisplayShowTable}
                      showPortfolioName={showPortfolioName}
                      onShowPortfolioNameChange={setShowPortfolioName}
                    />
                  )}

                  {/* Portfolio X-Ray Settings */}
                  {component?.componentType === "portfolio_xray" && (
                    <SettingsXrayForm
                      flowLanguage={flowLanguage}
                      hasXRayAccess={hasXRayAccess}
                      displayMode={xrayDisplayMode}
                      onDisplayModeChange={setXrayDisplayMode}
                      showChart={xrayShowChart}
                      onShowChartChange={setXrayShowChart}
                      showTable={xrayShowTable}
                      onShowTableChange={setXrayShowTable}
                      enableFollowupComparison={xrayEnableFollowupComparison}
                      onEnableFollowupComparisonChange={setXrayEnableFollowupComparison}
                    />
                  )}

                  {/* Product Costs Settings — the source only matters on the
                      portfolio pages of followup flows; the global view has no
                      existing portfolio */}
                  {component?.componentType === "product_costs" &&
                    pageType === "portfolio" &&
                    flowSupportsFollowup && (
                      <SettingsProductCostsForm
                        flowLanguage={flowLanguage}
                        portfolioSource={productCostsSource}
                        onPortfolioSourceChange={setProductCostsSource}
                      />
                    )}

                  {/* Web page (URL) Settings */}
                  {component?.componentType === "iframe" && (
                    <IframeEditor
                      url={iframeUrl}
                      height={iframeHeight}
                      aspectRatio={iframeAspectRatio}
                      flowLanguage={flowLanguage}
                      onUrlChange={setIframeUrl}
                      onHeightChange={setIframeHeight}
                      onAspectRatioChange={setIframeAspectRatio}
                    />
                  )}

                  {/* YouTube Video Settings */}
                  {component?.componentType === "youtube_video" && (
                    <YoutubeVideoEditor
                      url={youtubeUrl}
                      flowLanguage={flowLanguage}
                      onChange={setYoutubeUrl}
                    />
                  )}

                  {/* Image Settings */}
                  {component?.componentType === "image" && (
                    <ImageEditor
                      url={imageUrl}
                      flowLanguage={flowLanguage}
                      onChange={setImageUrl}
                    />
                  )}

                  {/* Goal Input Settings (QP-187 — optional description toggle) */}
                  {component?.componentType === "goal_input" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="goal-input-show-description" className="text-base">
                            {t.showGoalDescription}
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            {t.showGoalDescriptionHelp}
                          </p>
                        </div>
                        <Switch
                          id="goal-input-show-description"
                          checked={goalInputShowDescription}
                          onCheckedChange={setGoalInputShowDescription}
                        />
                      </div>
                    </div>
                  )}

                  {/* QL-281 — Goal List: description toggle for the add/edit modal */}
                  {component?.componentType === "goal_list" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="goal-list-show-description" className="text-base">
                            {t.showGoalDescription}
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            {t.showGoalDescriptionHelp}
                          </p>
                        </div>
                        <Switch
                          id="goal-list-show-description"
                          checked={goalListShowDescription}
                          onCheckedChange={setGoalListShowDescription}
                        />
                      </div>
                    </div>
                  )}

                  {/* QP-179 — Sustainability Status: allow-override toggle */}
                  {component?.componentType === "sustainability_status" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="sustainability-allow-override" className="text-base">
                            {t.sustainabilityAllowOverride}
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            {t.sustainabilityAllowOverrideHelp}
                          </p>
                        </div>
                        <Switch
                          id="sustainability-allow-override"
                          checked={sustainabilityStatusAllowOverride}
                          onCheckedChange={setSustainabilityStatusAllowOverride}
                        />
                      </div>
                    </div>
                  )}

                  {/* QL-13 — Quiz: the questions and pass rules, authored here */}
                  {component?.componentType === "quiz" && (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">{t.quizSetupHelp}</p>
                      <QuizSettingsEditor
                        value={quizSettings}
                        onChange={setQuizSettings}
                        language={flowLanguage}
                      />
                    </div>
                  )}

                  {/* QP-172 — Portfolio List: toggle columns */}
                  {component?.componentType === "portfolio_list" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="portfolio-list-show-amount">
                          {t.portfolioListShowAmount}
                        </Label>
                        <Switch
                          id="portfolio-list-show-amount"
                          checked={portfolioListShowAmount}
                          onCheckedChange={setPortfolioListShowAmount}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="portfolio-list-show-frequency">
                          {t.portfolioListShowFrequency}
                        </Label>
                        <Switch
                          id="portfolio-list-show-frequency"
                          checked={portfolioListShowFrequency}
                          onCheckedChange={setPortfolioListShowFrequency}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="portfolio-list-show-dates">
                          {t.portfolioListShowDates}
                        </Label>
                        <Switch
                          id="portfolio-list-show-dates"
                          checked={portfolioListShowDates}
                          onCheckedChange={setPortfolioListShowDates}
                        />
                      </div>
                    </div>
                  )}

                  {/* Info Modal Settings */}
                  {component?.componentType === "modal" && (
                    <ModalEditor
                      buttonLabel={modalButtonLabel}
                      title={modalTitle}
                      content={modalContent}
                      flowLanguage={flowLanguage}
                      onButtonLabelChange={setModalButtonLabel}
                      onTitleChange={setModalTitle}
                      onContentChange={setModalContent}
                    />
                  )}

                  {/* File Download Settings */}
                  {component?.componentType === "file" && (
                    <FileEditor
                      label={fileLabel}
                      fileName={fileFileName}
                      flowLanguage={flowLanguage}
                      onLabelChange={setFileLabel}
                      onFileChange={(s3Key, fileName) => {
                        setFileS3Key(s3Key);
                        setFileFileName(fileName);
                      }}
                    />
                  )}

                  {/* Portfolio Input Settings (schedule/amount + model/custom toggle) */}
                  {component?.componentType === "portfolio_input" && (
                    <SettingsPortfolioInputForm
                      flowLanguage={flowLanguage}
                      allowedPortfolioTypes={portfolioInputAllowedPortfolioTypes}
                      onAllowedPortfolioTypesChange={setPortfolioInputAllowedPortfolioTypes}
                      defaultPortfolioType={portfolioInputDefaultPortfolioType}
                      onDefaultPortfolioTypeChange={setPortfolioInputDefaultPortfolioType}
                      showPortfolioName={portfolioInputShowPortfolioName}
                      onShowPortfolioNameChange={setPortfolioInputShowPortfolioName}
                      showDateInputs={portfolioInputShowDateInputs}
                      onShowDateInputsChange={setPortfolioInputShowDateInputs}
                    />
                  )}

                  {/* Cost Chart Settings */}
                  {component?.componentType === "cost_chart" && (
                    <SettingsCostChartForm
                      flowLanguage={flowLanguage}
                      displayMode={costChartDisplayMode}
                      onDisplayModeChange={setCostChartDisplayMode}
                      chartType={costChartType}
                      onChartTypeChange={setCostChartType}
                      projectionYears={costChartProjectionYears}
                      onProjectionYearsChange={setCostChartProjectionYears}
                    />
                  )}

                  {/* Cost Table Settings */}
                  {component?.componentType === "cost_table" && (
                    <SettingsCostTableForm
                      flowLanguage={flowLanguage}
                      showHoldingPeriod={costTableShowHoldingPeriod}
                      onShowHoldingPeriodChange={setCostTableShowHoldingPeriod}
                      holdingPeriodYears={costTableHoldingPeriodYears}
                      onHoldingPeriodYearsChange={setCostTableHoldingPeriodYears}
                    />
                  )}

                  {/* Asset Allocation Settings */}
                  {component?.componentType === "asset_allocation" && (
                    <SettingsAssetAllocationForm
                      flowLanguage={flowLanguage}
                      showChart={assetAllocShowChart}
                      onShowChartChange={setAssetAllocShowChart}
                      showTable={assetAllocShowTable}
                      onShowTableChange={setAssetAllocShowTable}
                      enableFollowupComparison={assetAllocEnableFollowupComparison}
                      onEnableFollowupComparisonChange={setAssetAllocEnableFollowupComparison}
                    />
                  )}

                  {/* Asset Class Forecast Settings (QL-315) */}
                  {component?.componentType === "asset_class_forecast" && (
                    <HorizonChartConfigForm
                      idPrefix="acf"
                      chart="asset_class_forecast"
                      flowLanguage={flowLanguage}
                      showChart={acfShowChart}
                      onShowChartChange={setAcfShowChart}
                      showTable={acfShowTable}
                      onShowTableChange={setAcfShowTable}
                      enabledHorizons={acfEnabledHorizons}
                      onEnabledHorizonsChange={setAcfEnabledHorizons}
                      defaultHorizon={acfDefaultHorizon}
                      onDefaultHorizonChange={setAcfDefaultHorizon}
                      framed={false}
                    />
                  )}

                  {/* Volatility Chart Settings (QL-314) */}
                  {component?.componentType === "volatility_chart" && (
                    <HorizonChartConfigForm
                      idPrefix="vc"
                      chart="volatility_chart"
                      flowLanguage={flowLanguage}
                      enabledHorizons={vcEnabledHorizons}
                      onEnabledHorizonsChange={setVcEnabledHorizons}
                      defaultHorizon={vcDefaultHorizon}
                      onDefaultHorizonChange={setVcDefaultHorizon}
                      showTable={vcShowTable}
                      onShowTableChange={setVcShowTable}
                      framed={false}
                    />
                  )}

                  {/* Equity Share Chart Settings (QL-313) */}
                  {component?.componentType === "equity_share_chart" && (
                    <HorizonChartConfigForm
                      idPrefix="esc"
                      chart="equity_share_chart"
                      flowLanguage={flowLanguage}
                      enabledHorizons={escEnabledHorizons}
                      onEnabledHorizonsChange={setEscEnabledHorizons}
                      defaultHorizon={escDefaultHorizon}
                      onDefaultHorizonChange={setEscDefaultHorizon}
                      showTable={escShowTable}
                      onShowTableChange={setEscShowTable}
                      framed={false}
                    />
                  )}

                  {/* Expected Value Settings */}
                  {component?.componentType === "expected_value" && (
                    <SettingsExpectedValueForm
                      flowLanguage={flowLanguage}
                      showChart={expectedValueShowChart}
                      onShowChartChange={setExpectedValueShowChart}
                      showSummaryTable={expectedValueShowSummaryTable}
                      onShowSummaryTableChange={setExpectedValueShowSummaryTable}
                      showYearlyTable={expectedValueShowYearlyTable}
                      onShowYearlyTableChange={setExpectedValueShowYearlyTable}
                      compareWithBank={expectedValueCompareWithBank}
                      onCompareWithBankChange={setExpectedValueCompareWithBank}
                    />
                  )}

                  {/* Portfolio Forecast Settings */}
                  {component?.componentType === "portfolio_forecast" && (
                    <SettingsPortfolioForecastForm
                      flowLanguage={flowLanguage}
                      showStatistics={forecastShowStatistics}
                      onShowStatisticsChange={setForecastShowStatistics}
                      showTable={forecastShowTable}
                      onShowTableChange={setForecastShowTable}
                      compareWithBank={forecastCompareWithBank}
                      onCompareWithBankChange={setForecastCompareWithBank}
                    />
                  )}

                  {/* Financial Situation Settings */}
                  {component?.componentType === "financial_situation" && (
                    <SettingsFinancialSituationForm
                      flowLanguage={flowLanguage}
                      showIncome={finSitShowIncome}
                      onShowIncomeChange={setFinSitShowIncome}
                      showAssets={finSitShowAssets}
                      onShowAssetsChange={setFinSitShowAssets}
                      showLiabilities={finSitShowLiabilities}
                      onShowLiabilitiesChange={setFinSitShowLiabilities}
                      showCommitments={finSitShowCommitments}
                      onShowCommitmentsChange={setFinSitShowCommitments}
                      showCapacitySection={finSitShowCapacitySection}
                      onShowCapacitySectionChange={setFinSitShowCapacitySection}
                      capacityConfiguredInModel={capacityConfiguredInModel}
                      showStability={finSitShowStability}
                      onShowStabilityChange={setFinSitShowStability}
                      showExpectingChanges={finSitShowExpectingChanges}
                      onShowExpectingChangesChange={setFinSitShowExpectingChanges}
                      showCapacity={finSitShowCapacity}
                      onShowCapacityChange={setFinSitShowCapacity}
                      showSummary={finSitShowSummary}
                      onShowSummaryChange={setFinSitShowSummary}
                    />
                  )}

                  {/* Style/Title/Description Settings for various components */}
                  {hasStyleSettings && (
                    <SettingsStyleForm
                      flowLanguage={flowLanguage}
                      style={componentStyle}
                      onStyleChange={setComponentStyle}
                      title={componentTitle}
                      onTitleChange={setComponentTitle}
                      description={componentDescription}
                      onDescriptionChange={setComponentDescription}
                    />
                  )}

                  {/* Sumsub Liveness Verification (public pages only) */}
                  {component?.componentType === "sumsub_verification" &&
                    pageVisibility === "public" && (
                      <SumSubVerificationConfigForm
                        flowLanguage={flowLanguage}
                        livenessVerification={sumsubLivenessVerification}
                        onLivenessVerificationChange={setSumsubLivenessVerification}
                      />
                    )}

                  {/* Suitability Settings for Custom Portfolio */}
                  {component?.componentType === "custom_portfolio" && (
                    <SettingsCustomPortfolioForm
                      flowLanguage={flowLanguage}
                      onlyAllowSuitable={customPortfolioOnlyAllowSuitable}
                      onOnlyAllowSuitableChange={setCustomPortfolioOnlyAllowSuitable}
                    />
                  )}
                </TabsContent>

                {/* Fields Tab for Custom Portfolio */}
                {component?.componentType === "custom_portfolio" && (
                  <TabsContent value="fields" className="space-y-4 mt-4">
                    <SettingsCustomPortfolioFieldsForm
                      flowLanguage={flowLanguage}
                      showIsin={customPortfolioShowIsin}
                      onShowIsinChange={setCustomPortfolioShowIsin}
                      showProductType={customPortfolioShowProductType}
                      onShowProductTypeChange={setCustomPortfolioShowProductType}
                    />
                  </TabsContent>
                )}

                {/* Content tab for the embed component — authoring the HTML,
                    by hand or with AI, sits apart from how it is framed. */}
                {component?.componentType === "embed" && (
                  <TabsContent value="html" className="space-y-4 mt-4">
                    <EmbedEditor
                      html={embedHtml}
                      flowLanguage={flowLanguage}
                      onChange={setEmbedHtml}
                      frameStyle={componentStyle}
                      title={componentTitle}
                      description={componentDescription}
                    />
                  </TabsContent>
                )}

                <TabsContent value="conditional" className="space-y-4 mt-4">
                  <ConditionalVisibilityFields
                    enabled={conditionalEnabled}
                    onEnabledChange={setConditionalEnabled}
                    sourceFieldId={sourceFieldId}
                    onSourceFieldIdChange={handleSourceFieldChange}
                    operator={operator}
                    onOperatorChange={setOperator}
                    value={conditionValue}
                    onValueChange={setConditionValue}
                    availableFields={availableFields}
                    clientCustomFields={clientCustomFields}
                    disabled={loading}
                    flowLanguage={flowLanguage}
                    label={t.conditionalLabel}
                    description={t.conditionalDescription}
                    pageType={pageType}
                    portfolioCollectionId={portfolioCollectionId}
                    isSourceExcluded={isConditionSourceExcluded}
                    flowId={flowId}
                  />
                </TabsContent>
              </Tabs>
            ) : (
              // Field components only have conditional display
              <ConditionalVisibilityFields
                enabled={conditionalEnabled}
                onEnabledChange={setConditionalEnabled}
                sourceFieldId={sourceFieldId}
                onSourceFieldIdChange={handleSourceFieldChange}
                operator={operator}
                onOperatorChange={setOperator}
                value={conditionValue}
                onValueChange={setConditionValue}
                availableFields={availableFields}
                clientCustomFields={clientCustomFields}
                disabled={loading}
                flowLanguage={flowLanguage}
                label={t.fieldConditionalLabel}
                description={t.fieldConditionalDescription}
                pageType={pageType}
                portfolioCollectionId={portfolioCollectionId}
                isSourceExcluded={isConditionSourceExcluded}
                flowId={flowId}
              />
            )}
          </DialogBody>

          <DialogScrollFooter>
            <DialogCancel disabled={loading}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={loading}>
              {t.save}
            </Button>
          </DialogScrollFooter>
        </form>
      </DialogScrollContent>
    </Dialog>
  );
}
